import { useState, useEffect, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Menu, X, LogOut, LayoutDashboard } from 'lucide-react';
import { useScrollDirection } from '@/hooks/useScrollDirection';
import { prefetchPlans } from '@/hooks/usePlans';
import { useAuth } from '@/contexts/AuthContext';

const AuthModal = lazy(() => import('@/components/auth/AuthModal').then(m => ({ default: m.AuthModal })));

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/menu', label: 'Menu' },
  { href: '/plans', label: 'Plans' },
  { href: '/plan-finder', label: 'Plan Finder' },
  { href: '/blog', label: 'Blog' },
  { href: '/affiliate', label: 'Affiliate Program' },
];

export function Navbar() {
  const { scrollDirection, scrollY } = useScrollDirection();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMounted, setAuthModalMounted] = useState(false);

  function openAuthModal() {
    setAuthModalMounted(true);
    setAuthModalOpen(true);
  }
  const { user, isAdmin, isAffiliate, isLoading, signOut } = useAuth();

  const isScrolled = scrollY > 10;
  const dashboardHref = isAdmin ? '/admin' : '/account';

  useEffect(() => {
    if (scrollY > 10) {
      setIsVisible(scrollDirection === 'up');
    } else {
      setIsVisible(true);
    }
  }, [scrollDirection, scrollY]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return href.startsWith('/#') ? false : location.pathname.startsWith(href);
  };

  async function handleLogout() {
    setIsOpen(false);
    await signOut();
    navigate('/');
  }

  return (
    <header
      className={`backdrop-blur-sm fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-lg' : 'shadow-md'
      } ${isVisible ? 'translate-y-0' : '-translate-y-full'}`}
      style={{ backgroundColor: '#243C7B' }}
    >
      {/* Announcement bar */}
      <div className="text-white py-2 text-center text-xs md:text-sm font-medium border-b border-white/10">
        Halal &bull; High Protein &bull; Free Delivery in GTA &bull; No HST
      </div>

      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 px-0">
          {/* Left: Home icon button, no wordmark */}
          <Link to="/" className="flex items-center justify-center w-12 h-14 touch-manipulation" aria-label="ProteinMeals home">
            <Home className="w-6 h-6 text-white" />
          </Link>

          <div className="flex-1" />

          {/* Right: user action + hamburger */}
          <div className="flex items-center gap-2">
            {isLoading ? null : user ? (
              <Link
                to={dashboardHref}
                className="hidden md:flex items-center gap-1.5 text-white hover:text-terracotta transition-colors font-medium text-sm"
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </Link>
            ) : (
              <button
                onClick={openAuthModal}
                className="border border-white bg-[#243C7B] text-white hover:bg-white hover:text-[#243C7B] px-4 py-1.5 h-auto rounded-full font-semibold text-sm transition-colors"
              >
                Sign In
              </button>
            )}

            <button
              onTouchEnd={(e) => { e.preventDefault(); setIsOpen(o => !o); }}
              onClick={() => setIsOpen(o => !o)}
              className="flex items-center justify-center w-12 h-14 touch-manipulation"
              aria-label="Toggle Navigation Menu"
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
            </button>
          </div>
        </div>

        {/* Universal dropdown — applies to all breakpoints */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 bg-white shadow-xl border-t border-gray-200 z-40">
            <div className="px-4 py-3 space-y-1 max-h-[80vh] overflow-y-auto">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onMouseEnter={link.href === '/plans' ? prefetchPlans : undefined}
                  onClick={() => setIsOpen(false)}
                  className={`block px-3 py-2.5 rounded-md text-base font-medium transition-colors ${
                    isActive(link.href) ? 'bg-[#243C7B]/10 text-[#243C7B]' : 'text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              <div className="border-t border-gray-100 my-2" />

              {!isLoading && user ? (
                <>
                  <Link
                    to={dashboardHref}
                    className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-800 hover:bg-gray-100 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    My Dashboard
                  </Link>
                  {isAffiliate && (
                    <Link
                      to="/affiliate/portal"
                      className="block px-3 py-2.5 rounded-md text-base font-semibold text-[#D45A35] hover:bg-gray-100 transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      Partner Portal
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="block px-3 py-2.5 rounded-md text-base font-bold text-[#243C7B] hover:bg-gray-100 transition-colors"
                      onClick={() => setIsOpen(false)}
                    >
                      Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full text-left px-3 py-2.5 rounded-md text-base font-medium text-red-600 hover:bg-gray-100 transition-colors"
                  >
                    <LogOut size={17} />
                    Log Out
                  </button>
                </>
              ) : (
                !isLoading && (
                  <button
                    onClick={() => { setIsOpen(false); openAuthModal(); }}
                    className="block w-full text-left px-3 py-2.5 rounded-md text-base font-semibold text-[#243C7B] hover:bg-gray-100 transition-colors"
                  >
                    Sign In
                  </button>
                )
              )}

              <div className="pt-1 pb-2">
                <Link
                  to="/plans"
                  className="block px-3 py-3 bg-[#D45A35] hover:bg-[#b05a42] text-white rounded-md text-base font-bold transition-colors text-center"
                  onClick={() => setIsOpen(false)}
                >
                  Pick Your Plan
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {authModalMounted && createPortal(
        <Suspense fallback={null}>
          <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} mode="both" />
        </Suspense>,
        document.body
      )}
    </header>
  );
}
